import { AlertTriangle, CalendarClock } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { Button } from "../../../components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "../../../components/ui/dialog";
import { cn } from "../../../lib/utils";
import { formatDateBR } from "../../../utils/format-utils";
import { useAlertaNp } from "../hooks/use-nota-promissoria";
import { useDispensaPorTela } from "../hooks/use-dispensa-por-tela";
import { NotaPromissoria } from "../types";
import { formatarCentavos, situacaoNp } from "../utils/vencimento";

const ROTA_CONTROLE = "/notas-promissorias";

const quandoVence = (np: NotaPromissoria): string => {
  const { dias } = situacaoNp(np);
  if (dias < 0) return `venceu há ${-dias} dia${dias === -1 ? "" : "s"}`;
  if (dias === 0) return "vence hoje";
  return `vence em ${dias} dia${dias === 1 ? "" : "s"}`;
};

function ItemNp({ np, critico }: { np: NotaPromissoria; critico: boolean }) {
  return (
    <li
      className={cn(
        "rounded-md border-l-4 bg-muted/40 px-3 py-2",
        critico ? "border-destructive" : "border-amber-500"
      )}
    >
      <div className="flex flex-wrap items-baseline justify-between gap-x-3">
        <span className="font-medium">
          NP {np.id_contrato} — {np.nome_sacado}
        </span>
        <span className={cn("text-sm font-medium", critico ? "text-destructive" : "text-amber-600")}>
          {quandoVence(np)}
        </span>
      </div>
      <div className="text-sm text-muted-foreground">
        {formatarCentavos(np.valor_face)} · vencimento {formatDateBR(np.data_vencimento)}
      </div>
    </li>
  );
}

/**
 * Aviso global de NP vencida sem baixa ou vencendo em até 15 dias.
 *
 * Fica no Layout, então vale para todas as telas. Pode ser fechado, mas volta a
 * aparecer a cada troca de tela (ver useDispensaPorTela) — só não reaparece
 * quando a própria navegação partiu do botão de abrir o controle de NP.
 */
export function AlertaNotaPromissoria() {
  const { vencidas, aVencer, habilitado } = useAlertaNp();
  const { dispensado, dispensar, dispensarAoNavegar } = useDispensaPorTela();
  const navigate = useNavigate();

  const critico = vencidas.length > 0;
  const total = vencidas.length + aVencer.length;
  const aberto = habilitado && total > 0 && !dispensado;

  const irParaControle = () => {
    dispensarAoNavegar();
    navigate(ROTA_CONTROLE);
  };

  const titulo = critico
    ? `${vencidas.length} nota${vencidas.length > 1 ? "s" : ""} promissória${vencidas.length > 1 ? "s" : ""} vencida${vencidas.length > 1 ? "s" : ""} sem baixa`
    : `${aVencer.length} nota${aVencer.length > 1 ? "s" : ""} promissória${aVencer.length > 1 ? "s" : ""} a vencer`;

  return (
    <Dialog open={aberto} onOpenChange={(proximo) => !proximo && dispensar()}>
      <DialogContent className="max-w-xl">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            {critico ? (
              <AlertTriangle className="h-5 w-5 text-destructive" />
            ) : (
              <CalendarClock className="h-5 w-5 text-amber-500" />
            )}
            {titulo}
          </DialogTitle>
          <DialogDescription>
            {critico && aVencer.length > 0
              ? `Também há ${aVencer.length} nota${aVencer.length > 1 ? "s" : ""} vencendo nos próximos 15 dias.`
              : "Notas ainda não baixadas, com vencimento nos próximos 15 dias."}
          </DialogDescription>
        </DialogHeader>

        <ul className="max-h-[50vh] space-y-2 overflow-y-auto pr-1">
          {vencidas.map((np) => (
            <ItemNp key={np.id} np={np} critico />
          ))}
          {aVencer.map((np) => (
            <ItemNp key={np.id} np={np} critico={false} />
          ))}
        </ul>

        <DialogFooter>
          <Button variant="outline" onClick={dispensar}>
            Fechar
          </Button>
          <Button onClick={irParaControle}>Abrir controle de NP</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
